import type { TableProps, TabsProps } from "antd";
import { Button, DatePicker, Input, Tabs } from "antd";
import dayjs, { Dayjs } from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LicenseState } from "../../enum/lisence";
import dateIcon from "../../images/from-to-icon.svg";
import searchIcon from "../../images/search_icon.png";
import { DataType } from "../../models/interface/license.interface";
import { api } from "../../utils/api";
import { PAGE, PAGE_SIZE } from "../../utils/constants/common";
import TabActivated from "./LisenceMangement/TabActivated";
import TabRequesting from "./LisenceMangement/TabRequesting";
import TabDiscard from "./LisenceMangement/TabDiscard";
import UpdatePaymentModal from "./LisenceMangement/UpdatePaymentModal";

const AdminLisenceManagementPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [list, setList] = React.useState<DataType[]>([]);
  const [total, setTotal] = React.useState(0);
  const [listId, setListId] = React.useState<number[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [state, setState] = useState<string | null>(null);
  const [shipping, setShipping] = useState<string | null>(null);

  const page = Number(searchParams.get("page") || PAGE);
  const pageSize = Number(searchParams.get("pageSize") || PAGE_SIZE);
  const tab = searchParams.get("tab") || LicenseState.REQUESTING;

  const searchTerm = searchParams.get("search") || "";
  const startDate = searchParams.get("startDate") || "";
  const endDate = searchParams.get("endDate") || "";
  const sortField = searchParams.get("sortField") || "";
  const sortOrder = searchParams.get("sortOrder") || "";

  const [searchInput, setSearchInput] = React.useState(searchTerm);
  const [startDateInput, setStartDateInput] = useState<Dayjs | null>(
    startDate ? dayjs(startDate) : null,
  );

  const [endDateInput, setEndDateInput] = useState<Dayjs | null>(
    endDate ? dayjs(endDate) : null,
  );

  const dateFormat = "YYYY-MM-DD";
  const requestedAtSortOrder =
    sortField === "requestedAt"
      ? sortOrder === "asc"
        ? "ascend"
        : sortOrder === "desc"
          ? "descend"
          : null
      : null;

  const getData = useCallback(async () => {
    const res = await api.get(`api/licenses`, {
      params: {
        page: page,
        pageSize: pageSize,
        state: tab,
        search: searchTerm,
        startDate: startDate,
        endDate: endDate,
        sortField: sortField || undefined,
        sortOrder: sortOrder || undefined,
      },
    });
    setList(res.data.items);
    setTotal(res.data.total);
  }, [
    page,
    pageSize,
    tab,
    searchTerm,
    startDate,
    endDate,
    sortField,
    sortOrder,
  ]);

  useEffect(() => {
    setSearchInput(searchTerm);
    setStartDateInput(startDate ? dayjs(startDate) : null);
    setEndDateInput(endDate ? dayjs(endDate) : null);
  }, [searchTerm, startDate, endDate]);

  useEffect(() => {
    getData();
  }, [getData]);

  const onSearch = () => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (searchInput) {
        params.set("search", searchInput);
      } else {
        params.delete("search");
      }
      if (startDateInput) {
        params.set("startDate", startDateInput.startOf("day").toISOString());
      } else {
        params.delete("startDate");
      }
      if (endDateInput) {
        params.set("endDate", endDateInput.endOf("day").toISOString());
      } else {
        params.delete("endDate");
      }
      params.set("page", "1");
      return params;
    });
  };

  const onChange = (key: string) => {
    setSearchParams({ tab: key });
    setSearchInput("");
    setStartDateInput(null);
    setEndDateInput(null);
    setListId([]);
  };

  const handleTableChange: TableProps<DataType>["onChange"] = useCallback(
    (_: unknown, __: unknown, sorter: any) => {
      const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;
      const isRequestedAtSorter =
        currentSorter?.field === "requestedAt" ||
        currentSorter?.columnKey === "requestedAt";
      const isClearingRequestedAtSort =
        !currentSorter?.order && sortField === "requestedAt";

      if (!isRequestedAtSorter && !isClearingRequestedAtSort) return;

      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);

        if (currentSorter.order) {
          params.set("sortField", "requestedAt");
          params.set(
            "sortOrder",
            currentSorter.order === "ascend" ? "asc" : "desc",
          );
        } else {
          params.delete("sortField");
          params.delete("sortOrder");
        }
        params.set("page", "1");
        return params;
      });
    },
    [setSearchParams, sortField],
  );

  const rowSelection: TableProps<DataType>["rowSelection"] = useMemo(
    () => ({
      onChange: (_, selectedRows: DataType[]) => {
        setListId(selectedRows.map((item) => item.id));
      },
      selectedRowKeys: listId,
      hideSelectAll: true,
    }),
    [listId],
  );

  const openModalEditState = () => {
    setIsModalOpen(true);
  };

  const handleOk = async () => {
    setState(null);
    setShipping(null);
    setIsModalOpen(false);
    const data = {
      ids: listId,
      newState: state,
      newShippingStatus: shipping,
    };
    await api.put(`api/licenses/payment-status`, data);
    getData();
    setListId([]);
  };

  const handleCancel = () => {
    setState(null);
    setShipping(null);
    setIsModalOpen(false);
  };

  const items: TabsProps["items"] = useMemo<TabsProps["items"]>(
    () => [
      {
        key: LicenseState.REQUESTING,
        label: "申請中",
        children: (
          <TabRequesting
            key={tab}
            data={list}
            total={total}
            currentPage={page}
            pageSize={pageSize}
            requestedAtSortOrder={requestedAtSortOrder}
            onTableChange={handleTableChange}
          />
        ),
      },
      {
        key: LicenseState.ACTIVATED,
        label: "準備完了",
        children: (
          <TabActivated
            key={tab}
            data={list}
            total={total}
            rowSelection={rowSelection}
            currentPage={page}
            pageSize={pageSize}
            requestedAtSortOrder={requestedAtSortOrder}
            onTableChange={handleTableChange}
          />
        ),
      },
      {
        key: LicenseState.REVOKED,
        label: "廃棄",
        children: (
          <TabDiscard
            key={tab}
            data={list}
            total={total}
            currentPage={page}
            pageSize={pageSize}
            requestedAtSortOrder={requestedAtSortOrder}
            onTableChange={handleTableChange}
          />
        ),
      },
    ],
    [
      list,
      total,
      rowSelection,
      tab,
      page,
      pageSize,
      requestedAtSortOrder,
      handleTableChange,
    ],
  );

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex justify-between items-center h-[46px]">
        <div className="flex gap-3 h-full">
          <Input
            maxLength={255}
            placeholder="カメラSN・ハードウェアSN・代理店名で検索"
            className="base-input h-11 w-[424px]"
            suffix={<img src={searchIcon} className="h-6" alt="search icon" />}
            onChange={(e) => {
              setSearchInput(e.target.value);
            }}
            value={searchInput}
          />

          {tab === LicenseState.ACTIVATED && (
            <div className="h-full flex items-center gap-2">
              <DatePicker
                value={startDateInput}
                onChange={(date) => setStartDateInput(date)}
                maxDate={dayjs(endDateInput, dateFormat)}
                className="h-full"
              />
              <img src={dateIcon} alt="" />
              <DatePicker
                value={endDateInput}
                onChange={(date) => setEndDateInput(date)}
                minDate={dayjs(startDateInput, dateFormat)}
                className="h-full"
              />
            </div>
          )}
          <Button
            onClick={() => onSearch()}
            className="h-[46px] w-[52px] base-button"
          >
            検索
          </Button>
        </div>
        <div className="flex items-center gap-2 justify-end flex-1">
          <Button
            onClick={() => openModalEditState()}
            className="h-11 w-[150px] base-button"
            disabled={!(listId.length > 0)}
          >
            ステータス更新
          </Button>
        </div>
      </div>
      <Tabs
        activeKey={tab}
        items={items}
        onChange={onChange}
        className="tabs-custom"
      />
      <UpdatePaymentModal
        isModalOpen={isModalOpen}
        handleOk={handleOk}
        handleCancel={handleCancel}
        state={state}
        setState={setState}
        shipping={shipping}
        setShipping={setShipping}
      />
    </div>
  );
};

export default AdminLisenceManagementPage;
